import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { getGuidedTourConfig } from '../config/onboardingCatalog';

// Prod-silent, dev-visible telemetry (parity with Wave 1 #3/#4/#6/#9).
function tourTelemetry(event, payload) {
  if (typeof process !== 'undefined' && process.env && process.env.NODE_ENV === 'production') return;
  try { console.log('[tour_telemetry]', event, payload); } catch {}
}

// Is the element on-screen (not display:none, not inside a collapsed drawer)?
function isElementVisible(el) {
  if (!el) return false;
  if (el.offsetParent === null) return false;
  const rect = el.getBoundingClientRect();
  if (rect.width <= 0 || rect.height <= 0) return false;
  return true;
}

const GuidedTour = ({ onComplete, experienceMode = 'full', terms = {}, canUse = () => true }) => {
  const tourConfig = useMemo(
    () => getGuidedTourConfig({ experienceMode, terms, canUse }),
    [experienceMode, terms, canUse]
  );
  const tourSteps = tourConfig.steps;
  const tourVersion = tourConfig.version;
  const [currentStep, setCurrentStep] = useState(0);
  const [targetRect, setTargetRect] = useState(null);
  const drawerRequestedRef = useRef(false); // dedupe drawer-open dispatch per step
  const rafRef = useRef(null);
  // Reviewer GPT-5.4 P2 + Pedantic P1 + Angry P2 (convergent): track retry timers
  // so we can cancel them on step change / unmount and avoid stale setTargetRect.
  const retryTimersRef = useRef([]);
  const stepTokenRef = useRef(0); // increments on every step change; retries compare
  const dialogRef = useRef(null);
  const returnFocusRef = useRef(null);
  const reducedMotion = typeof window !== 'undefined'
    && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  const step = tourSteps[currentStep] || tourSteps[0];
  const isMobileViewport = typeof window !== 'undefined' && window.innerWidth < 900;
  const resolvedTarget = (isMobileViewport && 'mobileTarget' in step) ? step.mobileTarget : step.target;

  const clearAllRetries = useCallback(() => {
    while (retryTimersRef.current.length) {
      try { clearTimeout(retryTimersRef.current.pop()); } catch {}
    }
  }, []);

  // Pure rect measurement — no side effects, safe to call from scroll/resize listeners.
  // Reviewer Angry P1 + Pedantic P1: scrollIntoView was previously inside this
  // function and wired into `scroll` capture, creating a scroll-fight with the user.
  // Now scrollIntoView is only called from the step-change effect below.
  const measureTargetRect = useCallback(() => {
    if (!resolvedTarget) {
      setTargetRect(null);
      return { found: false, hidden: false };
    }
    let el = null;
    try { el = document.querySelector(resolvedTarget); } catch { el = null; }
    if (el && isElementVisible(el)) {
      const rect = el.getBoundingClientRect();
      setTargetRect({ top: rect.top, left: rect.left, width: rect.width, height: rect.height });
      return { found: true, hidden: false, el };
    }
    if (el && !isElementVisible(el)) {
      setTargetRect(null);
      return { found: true, hidden: true, el };
    }
    setTargetRect(null);
    return { found: false, hidden: false };
  }, [resolvedTarget]);

  // Step-change effect: fires once per step (and per resolvedTarget), runs the
  // "reveal" path that may scroll, open drawer, or retry with backoff.
  useEffect(() => {
    stepTokenRef.current += 1;
    const myToken = stepTokenRef.current;
    drawerRequestedRef.current = false;
    clearAllRetries();

    const revealAndMeasure = () => {
      if (myToken !== stepTokenRef.current) return; // step advanced, abort
      const { found, hidden, el } = measureTargetRect();
      if (found && !hidden && el) {
        // Scroll the target into view ONCE per step change, not per scroll event.
        try { el.scrollIntoView({ block: 'center', behavior: 'smooth' }); } catch {}
        return;
      }
      if (found && hidden) {
        // Target exists but is behind a closed drawer. Ask App to open it, then
        // retry with exponential backoff (Angry P1: slow phones sometimes exceed
        // the single 350ms window; retry up to ~1.5s then give up gracefully).
        if (!drawerRequestedRef.current) {
          drawerRequestedRef.current = true;
          try { window.dispatchEvent(new CustomEvent('tour-open-drawer')); } catch {}
        }
        const delays = [150, 350, 700, 1400];
        delays.forEach((ms) => {
          const t = setTimeout(() => {
            if (myToken !== stepTokenRef.current) return; // stale step; bail
            const retryEl = document.querySelector(resolvedTarget);
            if (retryEl && isElementVisible(retryEl)) {
              try { retryEl.scrollIntoView({ block: 'center', behavior: 'smooth' }); } catch {}
              const r = retryEl.getBoundingClientRect();
              setTargetRect({ top: r.top, left: r.left, width: r.width, height: r.height });
              tourTelemetry('tour_step_highlighted_mobile_fallback', { step: currentStep, selector: resolvedTarget, ms });
              clearAllRetries(); // success — drop any pending later retries
            } else if (ms === delays[delays.length - 1]) {
              tourTelemetry('tour_step_target_missing', { step: currentStep, selector: resolvedTarget, reason: 'hidden_after_drawer_open' });
            }
          }, ms);
          retryTimersRef.current.push(t);
        });
        return;
      }
      // Target selector but nothing found.
      tourTelemetry('tour_step_target_missing', { step: currentStep, selector: resolvedTarget });
    };

    revealAndMeasure();
    if (rafRef.current) cancelAnimationFrame(rafRef.current);
    rafRef.current = requestAnimationFrame(() => {
      if (myToken === stepTokenRef.current) measureTargetRect();
    });

    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      clearAllRetries();
    };
  }, [currentStep, resolvedTarget, measureTargetRect, clearAllRetries]);

  // Resize/scroll listeners only re-measure (cheap) — never re-scroll.
  useEffect(() => {
    const onResizeOrScroll = () => measureTargetRect();
    window.addEventListener('resize', onResizeOrScroll);
    window.addEventListener('scroll', onResizeOrScroll, true);
    return () => {
      window.removeEventListener('resize', onResizeOrScroll);
      window.removeEventListener('scroll', onResizeOrScroll, true);
    };
  }, [measureTargetRect]);

  useEffect(() => {
    // Fire a start event once per mount so we know how many tours were actually shown.
    tourTelemetry('tour_started', { tourVersion, experienceMode, totalSteps: tourSteps.length, mobile: isMobileViewport });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    returnFocusRef.current = document.activeElement;
    dialogRef.current?.focus();
    return () => {
      if (returnFocusRef.current?.focus) returnFocusRef.current.focus();
    };
  }, []);

  const handleNext = () => {
    if (currentStep < tourSteps.length - 1) {
      setCurrentStep(currentStep + 1);
    } else {
      tourTelemetry('tour_completed', { tourVersion, experienceMode, totalSteps: tourSteps.length });
      if (onComplete) onComplete({ completed: true, tourVersion, experienceMode });
    }
  };

  const handlePrev = () => {
    if (currentStep > 0) setCurrentStep(currentStep - 1);
  };

  const handleAbandon = useCallback(() => {
    tourTelemetry('tour_abandoned_at_step', { tourVersion, experienceMode, step: currentStep, title: step.title });
    if (onComplete) onComplete({ completed: false, tourVersion, experienceMode });
  }, [currentStep, step.title, onComplete, tourVersion, experienceMode]);

  // Reviewer Angry P2: keyboard-only users need Escape-to-dismiss.
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        handleAbandon();
        return;
      }
      if (e.key !== 'Tab' || !dialogRef.current) return;
      const focusable = dialogRef.current.querySelectorAll('button:not([disabled]), [tabindex]:not([tabindex="-1"])');
      if (focusable.length === 0) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [handleAbandon]);

  var cardTop = '50%';
  var cardLeft = '50%';
  var cardTransform = 'translate(-50%, -50%)';
  if (targetRect) {
    var belowSpace = window.innerHeight - (targetRect.top + targetRect.height);
    if (belowSpace > 220) {
      cardTop = (targetRect.top + targetRect.height + 16) + 'px';
      cardLeft = Math.min(Math.max(targetRect.left, 20), window.innerWidth - 420) + 'px';
      cardTransform = 'none';
    } else {
      cardTop = Math.max(targetRect.top - 220, 20) + 'px';
      cardLeft = Math.min(Math.max(targetRect.left, 20), window.innerWidth - 420) + 'px';
      cardTransform = 'none';
    }
  }

  return (
    <div
      ref={dialogRef}
      tabIndex={-1}
      style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, zIndex: 9999 }}
      data-testid="guided-tour"
      role="dialog"
      aria-modal="true"
      aria-labelledby="guided-tour-title"
    >
      {/* Backdrop */}
      <div
        style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.7)', zIndex: 9999 }}
        onClick={handleAbandon}
      />

      {/* Target highlight */}
      {targetRect && (
        <div
          data-testid="guided-tour-highlight"
          style={{
            position: 'fixed',
            top: targetRect.top - 4,
            left: targetRect.left - 4,
            width: targetRect.width + 8,
            height: targetRect.height + 8,
            borderRadius: '8px',
            boxShadow: '0 0 0 4px #2DD4BF, 0 0 20px rgba(0,229,255,0.4)',
            zIndex: 10000,
            pointerEvents: 'none',
            transition: reducedMotion ? 'none' : 'all 0.3s ease'
          }}
        />
      )}

      {/* Tour card */}
      <div
        data-testid="guided-tour-card"
        data-step={currentStep + 1}
        style={{
          position: 'fixed',
          top: cardTop,
          left: cardLeft,
          transform: cardTransform,
          background: 'var(--bg-card, #15151F)',
          border: '1px solid var(--border-color, #1E1E2B)',
          borderRadius: '16px',
          padding: '28px',
          maxWidth: '400px',
          width: '90%',
          zIndex: 10001,
          boxShadow: '0 20px 60px rgba(0,0,0,0.5)',
          transition: reducedMotion ? 'none' : 'all 0.3s ease'
        }}>
        {/* Step counter */}
        <div style={{ color: 'var(--text-dim, #9A9AB2)', fontSize: '12px', marginBottom: '8px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
          Step {currentStep + 1} of {tourSteps.length}
        </div>

        {/* Title */}
        <h3 id="guided-tour-title" style={{ color: 'var(--accent)', fontSize: '18px', fontWeight: 700, margin: '0 0 10px 0' }}>
          {step.title}
        </h3>

        {/* Description */}
        <p style={{ color: 'var(--text-secondary, #C3C4D6)', fontSize: '14px', lineHeight: '1.6', margin: '0 0 20px 0' }}>
          {step.description}
        </p>

        {/* Progress dots */}
        <div style={{ display: 'flex', gap: '6px', marginBottom: '20px', justifyContent: 'center' }}>
          {tourSteps.map((_, idx) => (
            <div
              key={idx}
              style={{
                width: idx === currentStep ? '20px' : '8px',
                height: '8px',
                borderRadius: '4px',
                background: idx === currentStep ? 'var(--accent)' : idx < currentStep ? '#2DD4BF88' : 'var(--border-color, #1E1E2B)',
                transition: reducedMotion ? 'none' : 'all 0.3s ease'
              }}
            />
          ))}
        </div>

        {/* Buttons */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <button
            onClick={handleAbandon}
            data-testid="guided-tour-skip"
            style={{
              padding: '6px 14px',
              background: 'transparent',
              border: 'none',
              color: 'var(--text-dim, #9A9AB2)',
              fontSize: '13px',
              cursor: 'pointer'
            }}
          >
            Skip Tour
          </button>
          <div style={{ display: 'flex', gap: '8px' }}>
            {currentStep > 0 && (
              <button
                onClick={handlePrev}
                data-testid="guided-tour-prev"
                style={{
                  padding: '8px 18px',
                  background: 'transparent',
                  border: '1px solid var(--border-input, #323248)',
                  color: 'var(--text-secondary, #C3C4D6)',
                  borderRadius: '8px',
                  fontSize: '13px',
                  cursor: 'pointer',
                  fontWeight: 600
                }}
              >
                Previous
              </button>
            )}
            <button
              onClick={handleNext}
              data-testid="guided-tour-next"
              style={{
                padding: '8px 24px',
                background: 'var(--accent)',
                border: 'none',
                color: 'var(--text-on-accent)',
                borderRadius: '8px',
                fontSize: '13px',
                cursor: 'pointer',
                fontWeight: 700
              }}
            >
              {currentStep === tourSteps.length - 1 ? 'Finish' : 'Next'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default GuidedTour;
